import React, { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import { getTransactionAccountBalance } from '@atlassian/temp-proxies';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ErrorComponents, useApplicationContext } from '@css-xp/base';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { TableLoader } from '@css-xp/ppc-page-loaders';
import { AccountBalanceTable } from './account-balance-table';
import { fetchTransactionAccountBalanceDetailsQuery } from './queries/fetch-account-balance';

export interface AccountBalanceProps {
  transactionAccountId: string;
  label: string;
  version?: number;
  currency?: string;
  isMultiCurrencyChangesEnabled?: boolean;
}

const extractInvoiceNumbers = (accountBalanceRecords: any) => {
  return accountBalanceRecords
    ?.filter((accountBalanceRecord: any) => accountBalanceRecord.invoiceNumber)
    .map((accountBalanceRecord: any) => accountBalanceRecord.invoiceNumber);
};

export const AccountBalance = ({
  transactionAccountId,
  label,
  version,
  currency,
  isMultiCurrencyChangesEnabled,
}: AccountBalanceProps) => {
  const [accountBalanceCurrency, setAccountBalanceCurrency] = useState<string | undefined>(currency);
  const [accountBalanceRecordData, setAccountBalanceRecordData] = useState<any>({
    loading: true,
    data: [],
    invoiceNumbers: [],
    error: '',
  });

  const context = useApplicationContext();
  const isCustomerAdvocate = context?.isCustomerAdvocate;

  const [fetchTransactionAccountBalanceDetails] = useLazyQuery(fetchTransactionAccountBalanceDetailsQuery, {
    variables: { id: transactionAccountId },
  });

  const fetchTransactionAccountBalance = useCallback(async () => {
    try {
      const response = await getTransactionAccountBalance(transactionAccountId, accountBalanceCurrency);
      if (!response || !response?.data) {
        setAccountBalanceRecordData({
          loading: false,
          error: '',
          data: [],
          invoiceNumbers: [],
        });
      } else {
        const accountBalanceRecords = response?.data;
        const invoiceNumbers = extractInvoiceNumbers(accountBalanceRecords);
        setAccountBalanceRecordData({
          loading: false,
          error: '',
          data: accountBalanceRecords,
          invoiceNumbers: invoiceNumbers,
        });
      }
    } catch (err: any) {
      setAccountBalanceRecordData({
        loading: false,
        error: err,
        data: [],
        invoiceNumbers: [],
      });
    }
  }, [accountBalanceCurrency]);

  const fetchTransactionAccountBalanceDetailsGraphQl = async () => {
    const { data, error } = await fetchTransactionAccountBalanceDetails();
    if (error) {
      setAccountBalanceRecordData({
        loading: false,
        error: error,
        data: [],
        invoiceNumbers: [],
      });
    }
    if (data?.transactionAccount?.transactionAccountBalanceEntity) {
      const accountBalanceRecords = data?.transactionAccount?.transactionAccountBalanceEntity;
      const invoiceNumbers = extractInvoiceNumbers(accountBalanceRecords);
      setAccountBalanceRecordData({
        loading: false,
        error: error,
        data: accountBalanceRecords,
        invoiceNumbers: invoiceNumbers,
      });
      return;
    }
    setAccountBalanceRecordData({
      loading: false,
      error: error,
      data: [],
      invoiceNumbers: [],
    });
  };

  useEffect(() => {
    if (isCustomerAdvocate) {
      fetchTransactionAccountBalanceDetailsGraphQl();
      return;
    }
    fetchTransactionAccountBalance();
  }, []);

  useEffect(() => {
    if (!isCustomerAdvocate) {
      fetchTransactionAccountBalance();
      return;
    }
  }, [accountBalanceCurrency]);

  if (accountBalanceRecordData.loading) {
    return version === 4 ? (
      <div className="account-balance-table-loader">
        <TableLoader hideActions columnsCount={7} />
      </div>
    ) : (
      <LoadingIndicator />
    );
  }

  if (accountBalanceRecordData.error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={accountBalanceRecordData.error} />;
  }

  return (
    <AccountBalanceTable
      transactionAccountId={transactionAccountId}
      label={label}
      accountBalanceRecords={accountBalanceRecordData?.data}
      invoiceNumbers={accountBalanceRecordData?.invoiceNumbers}
      version={version}
      defaultCurrency={accountBalanceCurrency}
      isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
      setAccountBalanceCurrency={setAccountBalanceCurrency}
      accountBalanceDataLoading={accountBalanceRecordData.loading}
    />
  );
};
